import * as React from 'react';
import { styleCategory as buttonStyle } from './ButtonStyles';
import {
  ThemePropsType,
  SubComponentPropsInterface,
} from 'supportchef-sdk-core';

const SIZE = {
  default: 'default',
  compact: 'compact',
};

export interface BaseButtonProps extends SubComponentPropsInterface {
  isFocused?: any;
  error?: any;
  disabled?: any;
  size?: any;
  children?: any;
  autoFocus?: any;
  inputRef?: any;
  onFocus?: any;
  onBlur?: any;
  onChange?: any;
  onClick?: any;
  value?: string;
  placeholder?: string;
  isLoading?: boolean;
  themedProps: ThemePropsType;
}

export interface BaseButtonComponentState {
  isFocused: boolean;
  active: boolean;
}

export class BaseButtonComponent extends React.Component<
  BaseButtonProps,
  BaseButtonComponentState
> {
  static componentName = 'BaseButtonComponent';

  static defaultProps = {
    'aria-label': null,
    'aria-labelledby': null,
    'aria-describedby': null,
    autoComplete: 'on',
    autoFocus: false,
    disabled: false,
    error: false,
    name: '',
    inputRef: React.createRef(),
    onBlur: () => {},
    onChange: () => {},
    onKeyDown: () => {},
    onKeyPress: () => {},
    onKeyUp: () => {},
    onFocus: () => {},
    overrides: {},
    placeholder: '',
    required: false,
    size: SIZE.default,
    type: 'button',
    isLoading: false,
  };

  constructor(props: BaseButtonProps) {
    super(props);

    this.state = {
      isFocused: false,
      active: false,
    };
  }

  componentDidMount() {
    const { autoFocus, inputRef } = this.props;
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }

  onFocus = (e) => {
    this.setState({ isFocused: true });
    this.props.onFocus(e);
  };

  onBlur = (e) => {
    this.setState({ isFocused: false });
    this.props.onBlur(e);
  };

  getButtonProps = (props) => {
    const {
      autoComplete,
      disabled,
      error,
      id,
      inputRef,
      ref,
      name,
      onChange,
      onClick,
      onKeyDown,
      onKeyPress,
      onKeyUp,
      placeholder,
      required,
      rows,
      type,
      value,
      autoFocus,
    } = props;

    return {
      ref: ref || inputRef,
      'aria-label': props['aria-label'],
      'aria-labelledby': props['aria-labelledby'],
      'aria-describedby': props['aria-describedby'],
      'aria-invalid': !!error,
      'aria-required': required,
      autoComplete,
      disabled,
      id,
      name,
      onBlur: this.onBlur,
      onChange,
      onClick,
      onFocus: this.onFocus,
      onKeyDown,
      onKeyPress,
      onKeyUp,
      placeholder,
      type,
      autoFocus,
      value: value ? value : '',
      // ...(type === CUSTOM_INPUT_TYPE.textarea ? { rows } : {}),
    };
  };

  // We set a className for being "active" because there is a bug
  // on iOS where the :active pseudoselector doesn't work if the component
  // isn't visible on load (which happens when you go back). In the future
  // we should conditionally execute this logic for the iOS platform only.
  changeActiveState = (newActive) => () => {
    const { isEditing } = this.props;

    if (!isEditing) {
      this.setState({ active: newActive });
    }
  };

  render() {
    const {
      isEditing,
      element,
      placeholder,
      children,
      isLoading,
      themedProps,
    } = this.props;
    const { isFocused, active } = this.state;
    const { StyledComponent: StyledButton, classNames } = themedProps;

    const inputSharedProps = { ...this.props, ...this.state };

    const inputProps = this.getButtonProps(inputSharedProps);

    const localClassNames = isLoading
      ? ['loading']
      : active
      ? ['active']
      : null;

    const allClassNames = localClassNames
      ? classNames.concat(localClassNames)
      : classNames;

    return (
      <StyledButton
        {...inputProps}
        tabIndex={isEditing ? -1 : undefined}
        className={allClassNames.join(' ')}
        onTouchStart={this.changeActiveState(true)}
        onTouchEnd={this.changeActiveState(false)}
      >
        <span>{children}</span>
      </StyledButton>
    );
  }
}

// export const BaseButtonComponent = coreObj =>
//   coreObj.withThemedComponent<
//     BaseButtonComponentNoTheme,
//     BaseButtonComponentProps
//   >(buttonStyle)(BaseButtonComponentNoTheme);
